import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import Toaster from "app/Toaster";
import { JM_ENVIRONMENT } from "app/Firebase";
import {
  getComponentRouteErrorLogMessage,
  getComponentRouteErrorMessage,
  getComponentRouteSignature,
  prepareComponentRouteResolution,
  resolveComponentRoute,
} from "./componentRouteResolver";
import { useRouteComponentNavigation } from "./routeComponentNavigation";
import { hasMountedScreenRoute } from "./screenRoute";

type RouteComponentExecutorProps = {
  enabled: boolean;
};

function getCurrentPathSearchHash() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function RouteComponentExecutor({
  enabled,
}: RouteComponentExecutorProps) {
  const location = useLocation();
  const {
    hasRoutedComponents,
    clearRoutedComponents,
    isMobileComponentStackRoute,
    openResolvedRoute,
  } = useRouteComponentNavigation();
  const executedRouteRef = useRef<string | null>(null);
  const ignoredEmptyRouteHrefRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const routeInput = {
      environment: JM_ENVIRONMENT,
      pathname: location.pathname,
      search: location.searchStr,
    };
    const signature = getComponentRouteSignature(routeInput);

    if (hasMountedScreenRoute(window.history.state)) {
      executedRouteRef.current = signature;
      return;
    }

    if (!signature) {
      const currentHref = getCurrentPathSearchHash();

      if (ignoredEmptyRouteHrefRef.current === currentHref) {
        ignoredEmptyRouteHrefRef.current = null;
        return;
      }

      if (hasRoutedComponents) {
        clearRoutedComponents();
      }

      executedRouteRef.current = null;
      return;
    }

    if (executedRouteRef.current === signature) {
      return;
    }

    const routeSignature = signature;

    if (isMobileComponentStackRoute()) {
      clearRoutedComponents();
      executedRouteRef.current = routeSignature;
      return;
    }

    let cancelled = false;

    async function execute() {
      try {
        const resolution = await prepareComponentRouteResolution(
          resolveComponentRoute(routeInput),
        );

        if (cancelled) {
          return;
        }

        if (resolution.type === "none") {
          executedRouteRef.current = routeSignature;
          return;
        }

        if (resolution.type === "error") {
          console.error(getComponentRouteErrorLogMessage(resolution.reason), {
            routeInput,
            resolution,
          });
          Toaster.error(
            getComponentRouteErrorMessage(resolution.reason),
          );
          executedRouteRef.current = routeSignature;
          return;
        }

        await openResolvedRoute({
          resolution,
          signature: routeSignature,
          onIgnoredEmptyRouteHref: (href) => {
            ignoredEmptyRouteHrefRef.current = href;
          },
        });
        executedRouteRef.current = routeSignature;
      } catch (error) {
        if (!cancelled) {
          console.error("Component link failed to open.", {
            routeInput,
            error,
          });
          Toaster.error("Failed to open link.");
          executedRouteRef.current = routeSignature;
        }
      }
    }

    void execute();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    clearRoutedComponents,
    hasRoutedComponents,
    isMobileComponentStackRoute,
    location.pathname,
    location.searchStr,
    openResolvedRoute,
  ]);

  return null;
}

export default RouteComponentExecutor;
